import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getQaSignoff, saveQaSignoff } from '../../api/client';
import { T } from '../../assets/theme';

// Mirrors AssessmentPage's TABS keys — one QA decision per assessment surface.
// Keep in sync with QA_SURFACE_IDS in backend/routes/project_crud.py.
const GROUPS = [
    {
        name: 'Setup & data',
        items: [
            { id: 'preview', code: 'PREVIEW', title: 'Obstacle Preview', help: 'Obstacle dataset for this aerodrome is complete, coordinates are plausible, and any import or parsing errors were resolved before assessment began.' },
        ],
    },
    {
        name: 'Annex 14 surfaces',
        items: [
            { id: 'ols', code: 'OLS', title: 'Annex 14 OLS', help: 'Every runway end assessed against the correct Obstacle Limitation Surfaces for its runway and approach classification, with penetrations reviewed.' },
            { id: 'ols-adg', code: 'OLS·ADG', title: 'OLS (ADG)', help: 'Aerodrome Reference/Design Group used matches the critical aircraft on file, and surfaces were re-checked against ADG-specific dimensions.' },
        ],
    },
    {
        name: 'Instrument procedures',
        items: [
            { id: 'pans', code: 'PA', title: 'Precision Approach (PANS-OPS)', help: 'If an ILS or other precision procedure is published, OCA/OCH and clearance surfaces are assessed per runway — otherwise marked N/A with the reason recorded.' },
            { id: 'npa', code: 'NPA', title: 'Non-Precision Approach', help: 'Every published NPA procedure (RNP, VOR, NDB, etc.) has been run, and its minima cross-checked against the Data Manager procedure record.' },
            { id: 'vss', code: 'VSS', title: 'Visual Segment Surface', help: 'VSS assessed for applicable approaches, with obstacle penetrations in the visual segment reviewed against the descent gradient.' },
            { id: 'circling', code: 'CIRCL', title: 'Visual Manoeuvring (Circling)', help: 'Circling assessed for every applicable aircraft category and cross-checked against the published circling minima.' },
        ],
    },
    {
        name: 'Departure / arrival',
        items: [
            { id: 'departure-ois', code: 'SID·OIS', title: 'Departure OIS', help: 'Every published SID has an OIS assessment on file, or is marked N/A with the reason (e.g. no SID published for this runway).' },
            { id: 'arrival-ois', code: 'STAR·OIS', title: 'Arrival OIS', help: 'Every published STAR has an OIS assessment on file, or is marked N/A with the reason.' },
            { id: 'msataa', code: 'MSA', title: 'MSA / TAA', help: 'Minimum sector altitudes and TAA sectors assessed and reconciled against the lowest MSA/TAA reference in Data Manager.' },
            { id: 'rnav-routes', code: 'RNAV', title: 'RNAV Routes', help: 'Applicable RNAV routes assessed for obstacle clearance, or marked N/A if none serve this aerodrome.' },
        ],
    },
    {
        name: 'Airspace & charts',
        items: [
            { id: 'airspace', code: 'ASP', title: 'Airspace', help: 'CTR, TMA, restricted or other relevant airspace considered for conflicts with the procedures assessed.' },
            { id: 'rmac', code: 'RMAC', title: 'RMAC', help: 'Relevant RMAC chart sectors reviewed and consistent with the obstacle data used elsewhere in the assessment.' },
            { id: 'cns', code: 'CNS·BRA', title: 'CNS BRA', help: 'Navaid obstacle assessment (BRA) completed for every navaid supporting the procedures assessed.' },
        ],
    },
    {
        name: 'Output',
        items: [
            { id: 'results', code: 'RESULTS', title: 'Results', help: 'Results page reconciled against every surface tab above — no missing entries, unresolved errors, or stale cached values.' },
            { id: 'report', code: 'REPORT', title: 'Report Preview', help: 'Report content matches the reviewed results: correct procedure names and minima, with no placeholder or draft text remaining.' },
        ],
    },
];
const ALL_ITEMS = GROUPS.reduce((acc, g) => acc.concat(g.items), []);

const VERDICTS = [
    { v: 'pass', label: 'Pass', help: 'No issues found on any surface', color: T.safe },
    { v: 'pass-obs', label: 'Pass, with observations', help: 'Minor items noted, none blocking', color: T.warn },
    { v: 'fail', label: 'Fail — rework required', help: 'One or more surfaces must be reassessed', color: T.danger },
];

function isItemComplete(rec) {
    if (!rec || !rec.status) return false;
    if ((rec.status === 'na' || rec.status === 'issue') && !(rec.reason || '').trim()) return false;
    return true;
}

function StatusPill({ kind, label, active, disabled, onClick }) {
    const colors = { ok: T.safe, na: T.warn, issue: T.danger };
    const c = colors[kind];
    return (
        <button onClick={onClick} disabled={disabled} style={{
            border: `1px solid ${active ? c : T.border}`,
            background: active ? `${c}22` : 'transparent',
            color: active ? c : T.muted,
            fontWeight: active ? 700 : 400,
            fontSize: 12, padding: '6px 12px', borderRadius: 100,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            whiteSpace: 'nowrap',
        }}>{label}</button>
    );
}

export default function QaSignoffPage({ project }) {
    const projectId = project?.id;
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
    const [gateError, setGateError] = useState('');
    const saveTimer = useRef(null);
    const latest = useRef(null);

    useEffect(() => {
        if (!projectId) return;
        setLoading(true);
        getQaSignoff(projectId).then(({ data }) => {
            setState(data);
            latest.current = data;
        }).finally(() => setLoading(false));
    }, [projectId]);

    const persist = useCallback((next, { completed } = {}) => {
        setSaveState('saving');
        setGateError('');
        return saveQaSignoff(projectId, {
            items: next.items,
            verdict: next.verdict,
            reviewer: next.reviewer,
            qa_date: next.qa_date,
            comments: next.comments,
            completed: completed !== undefined ? completed : next.completed,
        }).then(({ data }) => {
            setState(data);
            latest.current = data;
            setSaveState('saved');
            return data;
        }).catch((e) => {
            setSaveState('error');
            throw e;
        });
    }, [projectId]);

    function scheduleSave(next) {
        setState(next);
        latest.current = next;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => { persist(latest.current).catch(() => {}); }, 500);
    }

    function setItem(id, patch) {
        if (state.completed) return;
        const next = { ...state, items: { ...state.items, [id]: { ...state.items[id], ...patch } } };
        scheduleSave(next);
    }

    function toggleStatus(id, kind) {
        const cur = state.items[id]?.status;
        setItem(id, { status: cur === kind ? null : kind });
    }

    function setField(key, value) {
        if (state.completed) return;
        scheduleSave({ ...state, [key]: value });
    }

    function setVerdict(v) {
        if (state.completed) return;
        scheduleSave({ ...state, verdict: state.verdict === v ? null : v });
    }

    async function handleComplete() {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        try {
            await persist(state, { completed: true });
        } catch (e) {
            setGateError(e.response?.data?.detail || 'Could not complete QA sign-off.');
        }
    }

    async function handleReopen() {
        try {
            await persist(state, { completed: false });
        } catch (e) {
            setGateError(e.response?.data?.detail || 'Could not reopen QA sign-off.');
        }
    }

    function downloadRecord() {
        const lines = [];
        lines.push('TSELA OAS — ASSESSMENT SURFACE QA SIGN-OFF');
        lines.push('='.repeat(44));
        lines.push(`Project: ${project?.name || '—'}`);
        lines.push(`Aerodrome: ${project?.aerodrome || '—'}`);
        lines.push(`Assessed by: ${project?.assessor || '—'}`);
        lines.push('');
        GROUPS.forEach(group => {
            lines.push(`## ${group.name}`);
            group.items.forEach(it => {
                const rec = state.items[it.id] || {};
                const label = rec.status === 'ok' ? 'REVIEWED — OK' : rec.status === 'na' ? 'N/A' : rec.status === 'issue' ? 'ISSUE FOUND' : 'NOT REVIEWED';
                lines.push(`  [${label}] ${it.code} — ${it.title}`);
                if (rec.reason && rec.reason.trim()) lines.push(`    Note: ${rec.reason.trim()}`);
            });
            lines.push('');
        });
        lines.push('-'.repeat(44));
        const verdictLabel = { pass: 'PASS', 'pass-obs': 'PASS, WITH OBSERVATIONS', fail: 'FAIL — REWORK REQUIRED' }[state.verdict] || 'NOT YET SET';
        lines.push(`Overall verdict: ${verdictLabel}`);
        lines.push(`QA reviewer: ${state.reviewer || '—'}   Date: ${state.qa_date || '—'}`);
        if (state.comments && state.comments.trim()) { lines.push(''); lines.push('Comments:'); lines.push(state.comments.trim()); }
        if (state.completed) lines.push(`\nCompleted: ${state.completed_at}`);

        const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        const namePart = (project?.aerodrome || project?.name || 'project').toLowerCase().replace(/[^a-z0-9]+/g, '-');
        a.href = url; a.download = `qa-signoff-${namePart}.txt`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    if (loading || !state) {
        return <div style={{ padding: 32, color: T.muted }}>Loading QA checklist…</div>;
    }

    const doneCount = ALL_ITEMS.filter(it => isItemComplete(state.items[it.id])).length;
    const missing = ALL_ITEMS.filter(it => !isItemComplete(state.items[it.id]));
    const ready = missing.length === 0;

    return (
        <div style={{ padding: 24, overflowY: 'auto', height: '100%', boxSizing: 'border-box', maxWidth: 900, margin: '0 auto' }}>
            {/* Header + progress */}
            <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>
                        QA Sign-Off — {project?.name || project?.aerodrome || 'Checklist'}
                    </h2>
                    <span style={{ fontSize: 12, color: saveState === 'error' ? T.danger : T.muted }}>
                        {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Save failed' : ' '}
                    </span>
                </div>
                <p style={{ fontSize: 13, color: T.muted, maxWidth: '62ch', marginTop: 4 }}>
                    Every surface below needs an explicit decision before this project can be marked complete.
                    Saved automatically to this project as you work.
                </p>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
                    <div style={{ flex: 1, display: 'flex', gap: 3, height: 7 }}>
                        {ALL_ITEMS.map(it => {
                            const rec = state.items[it.id];
                            const complete = isItemComplete(rec);
                            const color = !complete ? T.badgeBg : rec.status === 'ok' ? T.safe : rec.status === 'na' ? T.warn : T.danger;
                            return <div key={it.id} style={{ flex: 1, borderRadius: 2, background: color }} />;
                        })}
                    </div>
                    <span style={{ fontSize: 12, color: T.muted, whiteSpace: 'nowrap' }}>
                        <strong style={{ color: T.white }}>{doneCount}/{ALL_ITEMS.length}</strong> resolved
                    </span>
                </div>
            </div>

            {state.completed && (
                <div style={{
                    background: 'rgba(46,201,139,0.1)', border: `1px solid ${T.safe}`, color: T.safe,
                    borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 18,
                }}>
                    ✓ QA review completed {state.completed_at ? `on ${new Date(state.completed_at).toLocaleString()}` : ''}. Use “Reopen” below to amend it.
                </div>
            )}

            {/* Groups */}
            {GROUPS.map(group => (
                <div key={group.name} style={{ marginBottom: 22 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                        {group.name}
                    </div>
                    <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                        {group.items.map((it, idx) => {
                            const rec = state.items[it.id] || { status: null, reason: '' };
                            const needsReason = rec.status === 'na' || rec.status === 'issue';
                            const missingReason = needsReason && !(rec.reason || '').trim();
                            return (
                                <div key={it.id} style={{
                                    padding: '14px 16px',
                                    borderTop: idx === 0 ? 'none' : `1px solid ${T.border}`,
                                }}>
                                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                                        <div style={{ display: 'flex', gap: 10, flex: 1, minWidth: 220 }}>
                                            <span style={{
                                                fontFamily: 'monospace', fontSize: 11, background: T.badgeBg, color: T.muted,
                                                padding: '3px 7px', borderRadius: 4, height: 'fit-content', flexShrink: 0,
                                            }}>{it.code}</span>
                                            <div>
                                                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{it.title}</div>
                                                <div style={{ fontSize: 12, color: T.muted, marginTop: 2, maxWidth: '62ch' }}>{it.help}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <StatusPill kind="ok" label="Reviewed — OK" active={rec.status === 'ok'} disabled={state.completed} onClick={() => toggleStatus(it.id, 'ok')} />
                                            <StatusPill kind="na" label="N/A" active={rec.status === 'na'} disabled={state.completed} onClick={() => toggleStatus(it.id, 'na')} />
                                            <StatusPill kind="issue" label="Issue found" active={rec.status === 'issue'} disabled={state.completed} onClick={() => toggleStatus(it.id, 'issue')} />
                                        </div>
                                    </div>
                                    {needsReason && (
                                        <div style={{ marginTop: 10 }}>
                                            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.04em', color: rec.status === 'issue' ? T.danger : T.warn, marginBottom: 4 }}>
                                                {rec.status === 'issue' ? 'What was found, and what needs to happen before this can pass' : 'Reason it does not apply here'}
                                            </div>
                                            <textarea
                                                value={rec.reason || ''}
                                                disabled={state.completed}
                                                onChange={e => setItem(it.id, { reason: e.target.value })}
                                                placeholder={rec.status === 'issue' ? 'e.g. Obstacle OBST-2201 penetrates the Type A OLS off RWY 21 — flagged to assessor for re-check.' : 'e.g. No STAR published for this aerodrome.'}
                                                style={{
                                                    width: '100%', minHeight: 52, resize: 'vertical', fontFamily: 'inherit',
                                                    fontSize: 12.5, padding: '7px 10px', borderRadius: 5, boxSizing: 'border-box',
                                                    background: T.bg0, color: T.white,
                                                    border: `1px solid ${missingReason ? T.danger : T.border}`,
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            {/* Sign-off */}
            <div style={{ background: T.bg1, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20, marginTop: 30, marginBottom: 40 }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800 }}>QA sign-off</h3>
                <p style={{ fontSize: 12.5, color: T.muted, marginTop: 0, marginBottom: 12 }}>
                    Once every surface above has a decision, record the overall verdict and close out the review.
                </p>

                {!ready && !state.completed && (
                    <div style={{ background: 'rgba(245,166,35,0.1)', border: `1px solid ${T.warn}`, color: T.warn, borderRadius: 6, padding: '9px 12px', fontSize: 12.5, marginBottom: 14 }}>
                        {missing.length} surface{missing.length === 1 ? '' : 's'} still need{missing.length === 1 ? 's' : ''} a decision before sign-off can be completed.
                    </div>
                )}
                {gateError && (
                    <div style={{ background: 'rgba(232,64,64,0.1)', border: `1px solid ${T.danger}`, color: T.danger, borderRadius: 6, padding: '9px 12px', fontSize: 12.5, marginBottom: 14 }}>
                        {gateError}
                    </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                    {VERDICTS.map(opt => {
                        const active = state.verdict === opt.v;
                        const disabled = !ready || state.completed;
                        return (
                            <button key={opt.v} onClick={() => setVerdict(opt.v)} disabled={disabled} style={{
                                flex: '1 1 160px', textAlign: 'left', borderRadius: 8, padding: '11px 13px',
                                background: active ? `${opt.color}18` : T.bg2,
                                border: `1.5px solid ${active ? opt.color : T.border}`,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                opacity: disabled ? 0.55 : 1,
                            }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: active ? opt.color : T.white }}>{opt.label}</div>
                                <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{opt.help}</div>
                            </button>
                        );
                    })}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>QA reviewer</div>
                        <input value={state.reviewer || ''} disabled={state.completed}
                            onChange={e => setField('reviewer', e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: T.bg0, color: T.white, fontSize: 13 }} />
                    </div>
                    <div>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>QA date</div>
                        <input type="date" value={state.qa_date || ''} disabled={state.completed}
                            onChange={e => setField('qa_date', e.target.value)}
                            style={{ width: '100%', boxSizing: 'border-box', padding: '7px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: T.bg0, color: T.white, fontSize: 13 }} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: 10, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Final comments (optional)</div>
                        <textarea value={state.comments || ''} disabled={state.completed}
                            onChange={e => setField('comments', e.target.value)}
                            placeholder="Anything the report author or next reviewer should know."
                            style={{ width: '100%', minHeight: 60, boxSizing: 'border-box', padding: '7px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: T.bg0, color: T.white, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    {!state.completed ? (
                        <button onClick={handleComplete} disabled={!ready || !state.verdict} style={{
                            background: T.blue, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px',
                            fontSize: 13, fontWeight: 700, cursor: (!ready || !state.verdict) ? 'not-allowed' : 'pointer',
                            opacity: (!ready || !state.verdict) ? 0.5 : 1,
                        }}>Complete QA review</button>
                    ) : (
                        <button onClick={handleReopen} style={{
                            background: 'transparent', color: T.muted, border: `1px solid ${T.border}`, borderRadius: 6,
                            padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}>Reopen</button>
                    )}
                    <button onClick={downloadRecord} style={{
                        background: 'transparent', color: T.blue, border: `1px solid ${T.blue}55`, borderRadius: 6,
                        padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}>Download record</button>
                </div>
            </div>
        </div>
    );
}
